// BadgeDisplay.jsx - Trophy Case and Badge Notification System (Win98 chrome styled)
import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { BADGES, BADGE_CATEGORIES, getBadgesByCategory } from '../../data/badgeDefinitions';
import BadgeIcon from '../icons/BadgeIcon';

// Strip the leading emoji glyph from a stored badge.name like "👼 First Review".
// Falls back to the full string if no leading emoji is present.
const stripBadgeNameEmoji = (name) => {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length <= 1) return name;
  return parts.slice(1).join(' ');
};

// Badge Notification Modal — a chrome dialog: raised bevel, gradient
// titlebar with a system close button, chrome-face body.
export function BadgeNotification({ badge, onClose }) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={200}
          recycle={false}
        />
      )}

      <div
        className="win95-window"
        role="dialog"
        aria-modal="true"
        aria-label="Achievement unlocked"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: '400px',
          textAlign: 'center',
          zIndex: 10000,
        }}
      >
        <div className="win95-titlebar" style={{ cursor: 'default' }}>
          <span className="win95-titlebar__label">Achievement unlocked</span>
          <div className="win95-titlebar__btns">
            <button
              type="button"
              className="win95-btn-sys"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div style={{ padding: '24px 28px' }}>
          <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
            <BadgeIcon id={badge.id || badge.badgeId} size={84} />
          </div>
          <h3 style={{
            margin: '0 0 10px',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 'var(--tc-head-sm)',
            letterSpacing: 'var(--track-head)',
            color: 'var(--chrome-outline)'
          }}>{stripBadgeNameEmoji(badge.name)}</h3>
          <p style={{
            margin: '0 0 20px',
            fontFamily: 'var(--font-chrome)',
            fontSize: 'var(--tc-ui)',
            color: 'var(--chrome-text-soft)'
          }}>{badge.description}</p>
          <button
            type="button"
            className="win95-btn win95-btn--default"
            onClick={onClose}
            style={{ minWidth: 90 }}
          >
            OK
          </button>
        </div>
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999
      }} onClick={onClose} />
    </>
  );
}

// Trophy Case Component
export function TrophyCase({ badges = [], userStats = {} }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredBadge, setHoveredBadge] = useState(null);

  // Get earned badge IDs
  const earnedBadgeIds = badges.map(b => b.id || b.badgeId);

  // Get all badges by category and sort with hidden ones at the end
  const allBadges = (selectedCategory === 'all'
    ? Object.values(BADGES)
    : getBadgesByCategory(selectedCategory))
    .sort((a, b) => {
      if (a.hidden && !b.hidden) return 1;
      if (!a.hidden && b.hidden) return -1;
      return 0;
    });

  // Headline numbers for the pane header — earned vs. defined.
  const totalDefined = Object.keys(BADGES).length;
  const earnedCount = Object.values(BADGES).filter(b => earnedBadgeIds.includes(b.id)).length;

  // Category labels for display (icons rendered separately via BadgeIcon)
  const categoryNames = {
    [BADGE_CATEGORIES.MILESTONES]: 'Review milestones',
    [BADGE_CATEGORIES.ENGAGEMENT]: 'Engagement',
    [BADGE_CATEGORIES.ACCURACY]: 'Accuracy & success',
    [BADGE_CATEGORIES.TIMING]: 'Consistency & timing',
    [BADGE_CATEGORIES.PATTERNS]: 'Rating patterns',
    [BADGE_CATEGORIES.GENERAL]: 'General',
    [BADGE_CATEGORIES.ELITE]: 'Elite tiers',
    [BADGE_CATEGORIES.STREAK]: 'Streaks',
    [BADGE_CATEGORIES.EASTER_EGG]: 'Easter eggs'
  };

  // Active filter = pressed chrome button (sunken bevel), so state never
  // reads by colour alone.
  const pressedStyle = {
    borderColor: 'var(--bevel-sunken)',
    boxShadow: 'var(--shadow-sunken)',
    background: 'var(--chrome-light)',
    fontWeight: 700,
  };

  return (
    <div className="lp-tab-page">
      {/* Pane header — chrome band replaces the old butter hero */}
      <div className="win95-pane-header">
        <div>
          <div className="win95-pane-header__context">
            {earnedCount} of {totalDefined} badges earned
          </div>
          <h2 className="win95-pane-header__title">Trophy case</h2>
        </div>
        <div className="win95-pane-header__stats">
          <div className="win95-statpanel">
            <div className="win95-statpanel__label">Earned</div>
            <div className="win95-statpanel__figure" style={{ color: 'var(--mb-aqua-deep)' }}>
              {earnedCount}
            </div>
          </div>
          <div className="win95-statpanel">
            <div className="win95-statpanel__label">Total</div>
            <div className="win95-statpanel__figure">{totalDefined}</div>
          </div>
        </div>
      </div>

      {/* Category filter — chrome toolbar of bevelled buttons */}
      <div className="win95-toolbar" role="group" aria-label="Badge categories" style={{ flexWrap: 'wrap' }}>
        <button
          type="button"
          className="win95-btn win95-btn--compact"
          aria-pressed={selectedCategory === 'all'}
          onClick={() => setSelectedCategory('all')}
          style={selectedCategory === 'all' ? pressedStyle : undefined}
        >
          All badges
        </button>
        <div className="win95-toolbar__sep" aria-hidden="true" />
        {Object.entries(categoryNames).map(([key, name]) => (
          <button
            key={key}
            type="button"
            className="win95-btn win95-btn--compact"
            aria-pressed={selectedCategory === key}
            onClick={() => setSelectedCategory(key)}
            style={selectedCategory === key ? pressedStyle : undefined}
          >
            <BadgeIcon category={key} size={14} />
            {name}
          </button>
        ))}
      </div>

      <section className="admin-section admin-section--paper" style={{ padding: '20px' }}>
      {/* Badge Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
        gap: '8px'
      }}>
        {allBadges.map(badge => {
          const isEarned = earnedBadgeIds.includes(badge.id);
          const earnedBadge = badges.find(b => (b.id || b.badgeId) === badge.id);
          const progress = badge.progress(userStats, { badges });

          return (
            <div
              key={badge.id}
              onMouseEnter={() => setHoveredBadge(badge.id)}
              onMouseLeave={() => setHoveredBadge(null)}
              style={{
                // Earned = raised chrome panel; locked = sunken well. The
                // state also reads via the grayscale icon and the
                // earned-date vs. locked/progress line — never colour alone.
                background: isEarned ? 'var(--chrome-face)' : 'var(--chrome-light)',
                border: '2px solid',
                borderColor: isEarned ? 'var(--bevel-raised)' : 'var(--bevel-sunken)',
                boxShadow: isEarned ? 'var(--shadow-raised)' : 'var(--shadow-sunken)',
                padding: '12px 8px',
                textAlign: 'center',
                position: 'relative',
                cursor: 'pointer',
                transition: 'none'
              }}
            >
              <div style={{
                marginBottom: '6px',
                display: 'flex',
                justifyContent: 'center',
                filter: !isEarned ? 'grayscale(100%)' : 'none',
                opacity: isEarned ? 1 : 0.55,
              }}>
                <BadgeIcon
                  id={badge.id}
                  locked={badge.hidden && !isEarned}
                  size={40}
                />
              </div>
              <div style={{
                fontFamily: 'var(--font-chrome)',
                fontSize: '11px',
                fontWeight: 700,
                marginBottom: '4px',
                color: 'var(--chrome-outline)',
                opacity: isEarned ? 1 : 0.7,
              }}>
                {badge.hidden && !isEarned ? 'Hidden badge' : stripBadgeNameEmoji(badge.name)}
              </div>

              {/* Locked state line — text, not just colour */}
              {!isEarned && (
                <div style={{
                  fontFamily: 'var(--font-chrome)',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--chrome-text-soft)',
                }}>
                  {progress > 0 ? `Locked · ${Math.round(progress * 100)}%` : 'Locked'}
                </div>
              )}

              {/* Progress bar for unearned badges — sunken chrome well */}
              {!isEarned && progress > 0 && (
                <div style={{
                  width: '100%',
                  height: '10px',
                  border: '2px solid',
                  borderColor: 'var(--bevel-sunken)',
                  background: 'var(--gnf-bg)',
                  marginTop: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${progress * 100}%`,
                    height: '100%',
                    background: 'var(--mb-magenta)',
                    transition: 'none'
                  }} />
                </div>
              )}

              {/* Earned Date */}
              {isEarned && earnedBadge && (
                <div style={{
                  fontFamily: 'var(--font-numeral)',
                  fontSize: '9px',
                  color: 'var(--chrome-text-soft)',
                  marginTop: '4px'
                }}>
                  {(() => {
                    const dateValue = earnedBadge.earnedAt || earnedBadge.earnedDate;
                    if (!dateValue) return 'Date unknown';

                    let date;
                    if (dateValue.toDate) {
                      date = dateValue.toDate();
                    } else if (dateValue instanceof Date) {
                      date = dateValue;
                    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
                      date = new Date(dateValue);
                    } else {
                      return 'Date unknown';
                    }

                    return isNaN(date.getTime()) ? 'Date unknown' : date.toLocaleDateString();
                  })()}
                </div>
              )}

              {/* Hover tooltip — classic tooltip yellow with a hard outline */}
              {hoveredBadge === badge.id && (
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--gnf-yellow-100)',
                  color: 'var(--chrome-outline)',
                  padding: '4px 8px',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  marginBottom: '4px',
                  zIndex: 10,
                  border: '1px solid var(--chrome-outline)',
                  fontFamily: 'var(--font-chrome)'
                }}>
                  {badge.hidden && !isEarned ? badge.hiddenDescription || '???' : badge.description}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats summary — chrome group box of sunken stat panels */}
      <fieldset className="win95-groupbox" style={{ marginTop: 20, minWidth: 0 }}>
        <legend>Badge statistics</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          {Object.entries(categoryNames).map(([category, name]) => {
            const categoryBadges = badges.filter(b => BADGES[b.id || b.badgeId]?.category === category);
            const totalInCategory = getBadgesByCategory(category).length;

            return (
              <div key={category} className="win95-statpanel" style={{ padding: '6px 10px', minWidth: 0 }}>
                <div className="win95-statpanel__label" style={{
                  whiteSpace: 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginBottom: '4px'
                }}>
                  <BadgeIcon category={category} size={14} />
                  {name}
                </div>
                <div className="win95-statpanel__figure">
                  {categoryBadges.length} / {totalInCategory}
                </div>
                <div style={{
                  width: '100%',
                  height: '10px',
                  border: '2px solid',
                  borderColor: 'var(--bevel-sunken)',
                  background: 'var(--gnf-bg)',
                  marginTop: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(categoryBadges.length / totalInCategory) * 100}%`,
                    height: '100%',
                    background: 'var(--mb-magenta)',
                    transition: 'none'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </fieldset>
      </section>
    </div>
  );
}
